/* eslint-disable unicorn/no-null */

import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import {
    Button,
    Divider,
    Input,
    Select,
    Switch,
    Textarea,
    TextInput,
    UnstyledButton
} from '@mantine/core';

import { useFormik } from 'formik';
import { processFormData } from '@newo/toolkit/bff';
import { TProject, TProjectData } from '@newo/api/gateway';
import getValidationSchema from '@/pages/ProjectsLegacy/components/ProjectForm/ProjectForm.schema.ts';
import MDEditor from '@/components/MDEditor';
import { useRegistriesQuery } from '@/services/designer/registries/registries.queries';
import { useRegistryItemVersionsQuery } from '@/services/bff/bff.queries';
import { useRegistryItemsQuery } from '@/services/designer/registry_items/registry_items.queries';

type ProjectFormProps = {
    project?: TProject;
    onClose?: () => void;
    onSubmit?: (data: TProjectData, projectId?: string) => Promise<void>;
};

const LATEST_VERSION = 'latest_version';

const ProjectForm: FC<ProjectFormProps> = ({ project, onClose, onSubmit }) => {
    const { data: registries = [] } = useRegistriesQuery();
    const isEditProject = !!project;
    const initialValues: TProjectData = {
        idn: project?.idn ?? '',
        title: project?.title ?? '',
        version: project?.version ?? '',
        description: project?.description ?? '',
        readme: project?.readme ?? '',
        is_auto_update_enabled: project?.is_auto_update_enabled ?? false,
        registry_idn: project?.registry_idn ?? 'production',
        registry_item_idn: project?.registry_item_idn ?? null,
        registry_item_version: project?.registry_item_version ?? LATEST_VERSION
    };

    const formik = useFormik({
        initialValues,
        validationSchema: getValidationSchema(isEditProject),
        async onSubmit(formData) {
            const normalized: TProjectData = {
                ...formData,
                registry_item_version:
                    formData.registry_item_version === LATEST_VERSION
                        ? undefined
                        : formData.registry_item_version
            };
            const data = processFormData(normalized, !!project);
            await onSubmit?.(data, project?.id);
            formik.setSubmitting(false);
        }
    });

    const { data } = useRegistryItemVersionsQuery(
        formik.values.registry_idn,
        formik.values.registry_item_idn,
        {
            page: 1,
            per: 100
        }
    );

    const selectedRegistryId = registries.find(
        (registry) => registry.idn === formik.values.registry_idn
    )?.id;

    const { data: registryItems } = useRegistryItemsQuery(selectedRegistryId);

    useEffect(() => {
        if (formik.values.registry_idn !== project?.registry_idn) {
            formik.setFieldValue('registry_item_idn', null);
            formik.setFieldValue(
                'registry_item_version',
                LATEST_VERSION,
                false
            );
            formik.setFieldTouched('registry_item_idn', false, false);
            formik.setFieldTouched('registry_item_version', false, false);
        }
    }, [formik.values.registry_idn, project?.registry_idn]);

    const handleCancel = () => {
        onClose?.();
    };

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('is_auto_update_enabled', event.target.checked);
    };

    const handleModuleChange = (value: string | null) => {
        formik.setFieldValue('registry_item_idn', value ?? null);
        formik.setFieldTouched('registry_item_idn', true, false);

        if (value) {
            formik.setFieldError('registry_item_idn', undefined);
        }
    };

    const handleRegistryIdnChange = (value: string | null) => {
        formik.setFieldValue('registry_idn', value ?? '');
        formik.setFieldValue('registry_item_idn', null);
        formik.setFieldValue('registry_item_version', LATEST_VERSION, true);
    };

    const handleVersionChange = (value: string | null) => {
        formik.setFieldValue('registry_item_version', value, false);
        formik.setFieldTouched('registry_item_version', true, false);
    };

    const registryItemOptions = useMemo(
        () => Array.from(new Set(registryItems?.map((item) => item.idn) ?? [])),
        [registryItems]
    );

    const versionOptions = useMemo(
        () =>
            (data?.items ?? []).map(({ version }) => ({
                label: version,
                value: version
            })),
        [data?.items]
    );

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Idn"
                name="idn"
                placeholder="Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <TextInput
                withAsterisk
                description="Project title"
                error={
                    formik.touched.title && formik.values.title
                        ? formik.errors.title
                        : undefined
                }
                label="Title"
                name="title"
                placeholder="Project title"
                value={formik.values.title}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Textarea
                description="Project description"
                error={
                    formik.touched.description && formik.values.description
                        ? formik.errors.description
                        : undefined
                }
                label="Description"
                name="description"
                placeholder="Project description"
                rows={4}
                value={formik.values.description ?? ''}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Input.Wrapper description="Project readme" label="Readme">
                <MDEditor
                    value={formik.values.readme ?? ''}
                    onChange={(value) => {
                        formik.setFieldValue('readme', value ?? '');
                    }}
                />
            </Input.Wrapper>

            <TextInput
                description="Project version"
                error={
                    formik.touched.version && formik.values.version
                        ? formik.errors.version
                        : undefined
                }
                label="Version"
                name="version"
                placeholder="Project version"
                value={formik.values.version}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Divider className="mt-2" label="Module" labelPosition="left" />

            <Select
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={registries.map((registry) => registry.idn)}
                label="Registry"
                name="registry_idn"
                placeholder="Select Registry"
                value={formik.values.registry_idn}
                onChange={handleRegistryIdnChange}
            />

            <Select
                clearable
                comboboxProps={{ withinPortal: false }}
                data={registryItemOptions}
                disabled={!formik.values.registry_idn}
                error={
                    formik.touched.registry_item_idn
                        ? formik.errors.registry_item_idn
                        : null
                }
                label="Module"
                name="registry_item_idn"
                placeholder="Select Module"
                value={formik.values.registry_item_idn}
                withAsterisk={isEditProject}
                onBlur={async () =>
                    formik.setFieldTouched('registry_item_idn', true)
                }
                onChange={handleModuleChange}
            />
            <Select
                clearable
                searchable
                data={[
                    { value: LATEST_VERSION, label: 'Latest version' },
                    ...versionOptions
                ]}
                disabled={!formik.values.registry_item_idn}
                error={
                    formik.touched.registry_item_version
                        ? formik.errors.registry_item_version
                        : null
                }
                label="Module version"
                name="registry_item_version"
                placeholder="Select Module version"
                value={formik.values.registry_item_version ?? null}
                onChange={handleVersionChange}
            />

            <Switch
                checked={formik.values.is_auto_update_enabled}
                label="Auto update enabled"
                name="is_auto_update_enabled"
                onChange={handleSwitchChange}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={!formik.dirty || !formik.isValid}
                    loading={formik.isSubmitting}
                    size="xs"
                    type="submit"
                >
                    {project ? 'Save' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default ProjectForm;
